import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    TextField,
} from '@mui/material';
import * as React from 'react';

import { DeveloperApiError } from '../api';
import { ErrorAlert } from '../components';
import { useAction } from './IntegrationsContext';

/** The typed name as the server compares it (I3 does the same): NFKD, combining marks dropped, case folded,
 *  trimmed. So "bokun" confirms Bókun, and a name typed in another case or composed differently still matches. */
export const normalizeConfirmation = (text: string) => text
    .normalize('NFKD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .trim();

/** A 502 `vendor_error` names the accounts whose webhooks the vendor had already removed (`_default`: Default). */
const uninstalledAccounts = (error: unknown): string[] => {
    const accounts = error instanceof DeveloperApiError ? (error.body as any)?.uninstalled_accounts : null;
    return Array.isArray(accounts) ? accounts.map((a) => (a === '_default' ? 'Default' : String(a))) : [];
};

/** The page's only dialog (plan §1 Remove): what stops, what stays, what to do at the vendor, and the
 *  name to type. The server checks the typed name too (`confirm`). */
const RemoveDialog = ({ open, title, lines, confirmation, confirmLabel, actionLabel = 'Remove', onRemove, onClose }: {
    open: boolean;
    title: string;
    lines: React.ReactNode[];
    /** What the user types: the system's name, or the account's slug. */
    confirmation: string;
    confirmLabel: string;
    actionLabel?: string;
    onRemove: (typed: string) => Promise<unknown>;
    onClose: () => void;
}) => {
    const [typed, setTyped] = React.useState('');
    const action = useAction();
    const matches = !!confirmation && normalizeConfirmation(typed) === normalizeConfirmation(confirmation);

    const remove = async (ev?: React.FormEvent) => {
        ev?.preventDefault();
        if (!matches) {
            return;
        }
        const outcome = await action.run(() => onRemove(typed.trim()));
        if (outcome.ok) {
            onClose();
        }
    };

    return (
        <Dialog
            open={open}
            onClose={action.busy ? undefined : onClose}
            aria-labelledby="remove-integration-title"
            fullWidth
            maxWidth="sm"
            TransitionProps={{ onExited: () => { setTyped(''); action.setError(null); } }}
            sx={{ zIndex: 10002 }}
        >
            <Box component="form" onSubmit={remove}>
                <DialogTitle id="remove-integration-title">{title}</DialogTitle>
                <DialogContent>
                    {lines.map((line, idx) => (
                        <DialogContentText key={idx} sx={{ mb: 1.5 }}>{line}</DialogContentText>
                    ))}
                    <TextField
                        autoFocus
                        fullWidth
                        size="small"
                        label={confirmLabel}
                        value={typed}
                        onChange={(ev) => setTyped(ev.target.value)}
                        disabled={action.busy}
                        inputProps={{ spellCheck: false, autoComplete: 'off', 'data-testid': 'remove-confirmation' }}
                        sx={{ mt: 1 }}
                    />
                    {action.error ? <ErrorAlert error={action.error} /> : null}
                    {uninstalledAccounts(action.error).length > 0 && (
                        <DialogContentText variant="body2" sx={{ mt: 1 }} data-testid="uninstalled-accounts">
                            Already uninstalled at the booking system: {uninstalledAccounts(action.error).join(', ')}. Its other webhooks and this connection are unchanged.
                        </DialogContentText>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose} disabled={action.busy}>Cancel</Button>
                    <Button
                        type="submit"
                        variant="contained"
                        color="error"
                        disabled={!matches || action.busy}
                        startIcon={action.busy ? <CircularProgress size={14} /> : undefined}
                    >
                        {actionLabel}
                    </Button>
                </DialogActions>
            </Box>
        </Dialog>
    );
};

export default RemoveDialog;
